/**
 * Care-type display fidelity for search cards (W1.2).
 * Peers come from generated taxonomy (shared/taxonomy/business_type_search_peers.json).
 */

import {
  BUSINESS_TYPE_NORMALIZATION,
  BUSINESS_TYPE_SEARCH_PEERS,
  CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY,
} from "../generated/careTaxonomy";
import { formatBusinessType } from "./providerFormatters";

export { BUSINESS_TYPE_SEARCH_PEERS };

/**
 * Resolve filter/row business_type (slug or display label) to a storage key
 * that can look up BUSINESS_TYPE_SEARCH_PEERS.
 */
export function normalizeBusinessTypeKey(
  value: string | null | undefined
): string {
  const raw = String(value || "").trim();
  if (!raw) {
    return "";
  }
  const fromDisplay = CARE_TYPE_DISPLAY_TO_BUSINESS_TYPE_KEY[raw];
  if (fromDisplay) {
    return fromDisplay;
  }
  const spaced = raw.toLowerCase().replace(/[\s-]+/g, "_");
  const collapsed = spaced.replace(/_/g, "");
  return (
    BUSINESS_TYPE_NORMALIZATION[spaced] ??
    BUSINESS_TYPE_NORMALIZATION[collapsed] ??
    spaced
  );
}

/**
 * True when the row's business type is a search peer of the active Care Type
 * filter (exact match is not a "peer").
 */
export function isCareTypeSearchPeer(
  providerBusinessType: string | null | undefined,
  filterBusinessType: string | null | undefined
): boolean {
  const filter = normalizeBusinessTypeKey(filterBusinessType);
  const provider = normalizeBusinessTypeKey(providerBusinessType);
  if (!filter || !provider || filter === provider) {
    return false;
  }
  const peers = BUSINESS_TYPE_SEARCH_PEERS[filter];
  return Boolean(peers?.includes(provider));
}

/**
 * Plan 32 B9: rare-suppress affinity for card chips.
 * Use the filter's affinity only when the row is a search peer of that filter
 * (e.g. home_care under home_health). Exact matches and non-peers keep the
 * row's own rare list.
 */
export function resolveRareAffinityBusinessType(
  providerBusinessType: string | null | undefined,
  filterBusinessType: string | null | undefined
): string | undefined {
  if (!isCareTypeSearchPeer(providerBusinessType, filterBusinessType)) {
    return undefined;
  }
  return normalizeBusinessTypeKey(filterBusinessType) || undefined;
}

/**
 * When the active Care Type filter expanded to peers, return a short note
 * if this provider is a peer (not an exact type match).
 */
export function getCareTypePeerNote(
  providerBusinessType: string | null | undefined,
  filterBusinessType: string | null | undefined
): string | null {
  if (!isCareTypeSearchPeer(providerBusinessType, filterBusinessType)) {
    return null;
  }
  const filter = normalizeBusinessTypeKey(filterBusinessType);
  const filterLabel = formatBusinessType(filter);
  return `${filterLabel} peer`;
}
